import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FolderOpen, Plus, Pencil, Trash2, Save, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GestaoAreas = () => {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAreas(); }, []);

  const fetchAreas = async () => {
    try {
      const res = await axios.get(`${API}/areas`);
      setAreas(res.data.areas);
    } catch { toast.error('Erro ao carregar áreas'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) { toast.error('Digite o nome da área'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/areas`, { label: newLabel }, { headers: getAdminHeaders() });
      toast.success('Área criada com sucesso!');
      setNewLabel('');
      setAdding(false);
      fetchAreas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar área');
    } finally { setSaving(false); }
  };

  const handleEdit = async (id) => {
    if (!editLabel.trim()) { toast.error('Digite o nome da área'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/areas/${id}`, { label: editLabel }, { headers: getAdminHeaders() });
      toast.success('Área atualizada!');
      setEditingId(null);
      setEditLabel('');
      fetchAreas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/areas/${id}`, { headers: getAdminHeaders() });
      toast.success('Área excluída!');
      fetchAreas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  if (loading) {
    return (<div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><p className="font-inter text-gray-500">Carregando...</p></div>);
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button data-testid="back-button" onClick={() => navigate(-1)} className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <FolderOpen className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Gestão de Áreas</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">{areas.length} áreas cadastradas</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Botão Adicionar */}
        {!adding ? (
          <Button
            data-testid="add-area-btn"
            onClick={() => setAdding(true)}
            className="w-full mb-6 bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-xl py-5 text-base font-semibold gap-2"
          >
            <Plus className="w-5 h-5" /> Nova Área
          </Button>
        ) : (
          <Card className="bg-white rounded-xl p-4 shadow-md border border-[#1A4D2E]/30 mb-6">
            <p className="font-nunito font-bold text-sm text-[#1A4D2E] mb-3">Nova Área</p>
            <div className="flex gap-2">
              <Input
                data-testid="new-area-input"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Ex: Recepção, Cozinha, Estacionamento..."
                className="flex-1 rounded-xl border-gray-200"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
              <Button
                data-testid="save-new-area"
                onClick={handleAdd}
                disabled={saving}
                size="sm"
                className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-xl px-4"
              >
                <Save className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setAdding(false); setNewLabel(''); }}
                className="rounded-xl"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Lista de Áreas */}
        <div className="space-y-2">
          {areas.map((area) => (
            <Card key={area.id} data-testid={`area-card-${area.id}`} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5] hover:shadow-md transition-all">
              {editingId === area.id ? (
                <div className="flex items-center gap-2">
                  <Input
                    data-testid={`edit-area-input-${area.id}`}
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="flex-1 rounded-xl border-gray-200"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleEdit(area.id)}
                  />
                  <Button
                    data-testid={`save-edit-${area.id}`}
                    onClick={() => handleEdit(area.id)}
                    disabled={saving}
                    size="sm"
                    className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-xl px-3"
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditingId(null); setEditLabel(''); }}
                    className="rounded-xl"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#F2F7F4] rounded-lg flex items-center justify-center">
                      <FolderOpen className="w-4 h-4 text-[#1A4D2E]" />
                    </div>
                    <div>
                      <p className="font-inter font-semibold text-sm text-[#1A4D2E]">{area.label}</p>
                      <p className="font-inter text-xs text-gray-400">Chave: {area.key}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`edit-area-${area.id}`}
                      onClick={() => { setEditingId(area.id); setEditLabel(area.label); }}
                      className="text-blue-500 hover:bg-blue-50 rounded-full p-2 transition-all"
                      title="Editar"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-area-${area.id}`}
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-2 transition-all"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir área "{area.label}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. Áreas com voluntários vinculados não podem ser excluídas.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(area.id)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
      <Navigation active="dashboard" />
    </div>
  );
};

export default GestaoAreas;
